import React, { useEffect, useMemo, useState } from "react";

// Firebase
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { COLLECTION_NAME } from "../../constants/endpoints";

// Library
import "react-toastify/dist/ReactToastify.css";
import { Bounce, toast } from "react-toastify";
import dayjs from "dayjs";

// Custom Component
import Table from "../../Components/Table/Table";
import PriceSurgeModal from "../../Components/PriceSurgeModal/PriceSurgeModal";
import ActionButton from "../../Components/Button/Button";

// Icons
import { FiEdit2, FiTrash } from "react-icons/fi";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import Loader from "../../Components/Loader/Loader";

const PriceSurge = () => {
  const [priceSurgeModalOpen, setPriceSurgeModalOpen] = useState(false);
  const [sortField, setSortField] = useState("startTime");
  const [sortOrder, setSortOrder] = useState("asc");
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);

  const onAddPriceSurge = () => {
    setEditData(null);
    setPriceSurgeModalOpen(true);
  };
  const onConfirm = async (data) => {
    try {
      // Use hours directly as numbers (0-23)
      const startTime = parseInt(data.startTime);
      const endTime = parseInt(data.endTime);

      const surgePricesSnapshot = await getDocs(
        collection(db, COLLECTION_NAME.surgePrice)
      );

      const hasOverlap = surgePricesSnapshot.docs.some((doc) => {
        if (data.docId && doc.id === data.docId) return false;

        const existingStart = doc.data().startTime;
        const existingEnd = doc.data().endTime;

        // Handle time overlap logic for 24-hour format
        // Check if time ranges overlap (considering day boundaries)
        // Allow consecutive time slots (e.g., 6-10 and 10-14 are allowed)
        const overlap =
          (startTime < existingEnd && endTime > existingStart) ||
          (startTime > endTime &&
            (startTime < existingEnd || endTime > existingStart)); // Handle day boundary crossing

        return overlap;
      });

      if (hasOverlap) {
        toast.error(
          "Time period overlaps with an existing price surge. Please choose a different time range.",
          {
            position: "top-right",
            autoClose: 5000,
            transition: Bounce,
          }
        );
        return;
      }

      // If no overlap, proceed with saving
      if (data.docId) {
        await updateDoc(doc(db, "SurgePrice", data.docId), {
          startTime: startTime,
          endTime: endTime,
          multiplier: parseFloat(data.multiplier),
        });
        toast.success("Surge Price Updated Successfully", {
          position: "top-right",
          autoClose: 5000,
          transition: Bounce,
        });
      } else {
        await addDoc(collection(db, COLLECTION_NAME.surgePrice), {
          startTime: startTime,
          endTime: endTime,
          multiplier: parseFloat(data.multiplier),
        });
        toast.success("Surge Price Added Successfully", {
          position: "top-right",
          autoClose: 5000,
          transition: Bounce,
        });
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
    } finally {
      setPriceSurgeModalOpen(false);
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const onDelete = (id) => async () => {
    await deleteDoc(doc(db, "SurgePrice", id));
    toast.success("Surge Price Deleted Successfully", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: (
          <div
            onClick={() => toggleSort("id")}
            className="cursor-pointer flex gap-1 items-center"
          >
            ID{" "}
            {sortField === "id" &&
              (sortOrder === "asc" ? <IoIosArrowUp /> : <IoIosArrowDown />)}
          </div>
        ),
        accessor: "id",
      },
      {
        Header: (
          <div
            onClick={() => toggleSort("startTime")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Start Time{" "}
            {sortField === "startTime" &&
              (sortOrder === "asc" ? <IoIosArrowUp /> : <IoIosArrowDown />)}
          </div>
        ),
        accessor: "startTime",
      },
      {
        Header: (
          <div
            onClick={() => toggleSort("endTime")}
            className="cursor-pointer flex gap-1 items-center"
          >
            End Time{" "}
            {sortField === "endTime" &&
              (sortOrder === "asc" ? <IoIosArrowUp /> : <IoIosArrowDown />)}
          </div>
        ),
        accessor: "endTime",
        Cell: ({ value }) => (value ? value : "-----"),
      },
      {
        Header: (
          <div
            onClick={() => toggleSort("multiplier")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Multiplier{" "}
            {sortField === "multiplier" &&
              (sortOrder === "asc" ? <IoIosArrowUp /> : <IoIosArrowDown />)}
          </div>
        ),
        accessor: "multiplier",
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="flex gap-2">
            <button
              className="text-primary hover:text-dazzledBlue text-lg"
              onClick={() => {
                setEditData(row.original);
                setPriceSurgeModalOpen(true);
              }}
            >
              <FiEdit2 />
            </button>
            <button
              className="text-red-600 hover:text-red-900 text-lg"
              onClick={onDelete(row.original.docId)}
            >
              <FiTrash />
            </button>
          </div>
        ),
      },
    ],
    [sortField, sortOrder]
  );

  // Helper function to format hour number to display string
  const formatHourFromNumber = (hourNumber) => {
    return `${hourNumber.toString().padStart(2, "0")}:00`;
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, COLLECTION_NAME.surgePrice),
      (snapshot) => {
        const fetchedData = snapshot.docs.map((doc, index) => {
          const data = doc.data();
          return {
            id: index + 1,
            startTime: formatHourFromNumber(data.startTime),
            endTime: formatHourFromNumber(data.endTime),
            multiplier: data.multiplier,
            docId: doc.id,
          };
        });

        const sortedData = [...fetchedData].sort((a, b) => {
          if (sortField === "id" || sortField === "multiplier") {
            if (sortOrder === "asc") {
              return a[sortField] - b[sortField];
            } else {
              return b[sortField] - a[sortField];
            }
          } else if (sortField === "startTime" || sortField === "endTime") {
            // Extract hour from formatted string (e.g., "14:00" -> 14)
            const aTime = parseInt(a[sortField].split(":")[0]);
            const bTime = parseInt(b[sortField].split(":")[0]);

            if (sortOrder === "asc") {
              return aTime - bTime;
            } else {
              return bTime - aTime;
            }
          } else {
            if (sortOrder === "asc") {
              return String(a[sortField]).localeCompare(String(b[sortField]));
            } else {
              return String(b[sortField]).localeCompare(String(a[sortField]));
            }
          }
        });

        setData(sortedData);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [sortField, sortOrder]);

  return (
    <div className="px-1 sm:px-1 md-px-4 min-h-screen">
      <div className="flex items-center justify-between mt-5 mb-5">
        <h1 className="text-2xl md:text-3xl font-bold text-primary">
          Price Surge
        </h1>
        <div className="flex">
          <ActionButton
            buttonText="Add Price Surge"
            onButtonClick={onAddPriceSurge}
          />
        </div>
      </div>

      {loading ? <Loader /> : <Table columns={columns} data={data} />}

      <PriceSurgeModal
        isOpen={priceSurgeModalOpen}
        onRequestClose={() => setPriceSurgeModalOpen(false)}
        onConfirm={onConfirm}
        editData={editData}
      />
    </div>
  );
};

export default PriceSurge;

